import type { FC } from "react";
import { BriefcaseIcon, MapPinIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { IconText } from "./icon-text";
import type { CardLayoutProps } from "./layout-props";
import { PractitionerAvatar } from "./practitioner-avatar";
import { PractitionerTags } from "./practitioner-tags";
import { ProfessionList } from "./profession-list";
import { SkeletonBar } from "./skeleton-bar";
import { SpecialtyPill } from "./specialty-pill";
import { VerifiedName } from "./verified-name";

const MapSideBarLayoutSkeleton: FC = () => (
  <div className="flex flex-col gap-12 p-24">
    <div className="flex w-full items-start gap-12">
      <div className="skeleton-bar-dark h-143 w-138 shrink-0 rounded-medium" />
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-8 self-stretch">
        <SkeletonBar tone="dark" className="h-28 w-3/5" />
        <SkeletonBar className="w-2/5" />
        <SkeletonBar className="w-1/3" />
      </div>
    </div>
    <div className="flex gap-8">
      <SkeletonBar className="h-36 w-1/4" />
      <SkeletonBar className="h-36 w-1/4" />
      <SkeletonBar className="h-36 w-1/3" />
    </div>
    <SkeletonBar className="h-32 w-full" />
  </div>
);

export const MapSideBarLayout: FC<CardLayoutProps> = ({ content, loading }) => {
  if (!content || loading) return <MapSideBarLayoutSkeleton />;

  const { name, verified, professions, location, years, photoUrl } = content;
  const { specialties, languages, formats } = content;

  return (
    <div className="flex flex-col gap-12 p-24">
      <div className="flex w-full items-start gap-12">
        <PractitionerAvatar name={name} photoUrl={photoUrl} className="h-143 w-138 shrink-0" />

        <div className="flex min-w-0 flex-1 flex-col justify-center gap-12 self-stretch">
          <VerifiedName name={name} verified={verified} className="min-w-0" nameClassName="min-w-0 truncate text-21" />

          <div className="flex flex-col gap-8">
            {professions.length > 0 && (
              <IconText icon={<BriefcaseIcon className="size-18" />} className="items-start text-16">
                <ProfessionList entries={professions} tagClassName="text-16" />
              </IconText>
            )}
            {years !== undefined && years > 0 && (
              <span className="text-16 text-neutral-black">{years}+ Year of Experience</span>
            )}
            {location && (
              <IconText icon={<MapPinIcon className="size-18" />} className="text-16">
                {location}
              </IconText>
            )}
          </div>
        </div>
      </div>

      <PractitionerTags languages={languages} formats={formats} hideIcons scrollable />
      <SpecialtyPill
        specialties={specialties}
        pill
        scrollable
        size="m"
        icon={<SparklesIcon className="size-24 shrink-0 text-brand-dark-blue" />}
        className="gap-8 px-8"
      />
    </div>
  );
};
